
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import ArtworkModal from '../../gallery/components/ArtworkModal';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Artwork {
  id: number;
  title: string;
  artist: string;
  technique: string;
  category: string;
  size: string;
  price: string;
  year: string;
  materials: string;
  description: string;
  image_url?: string;
  images?: string[];
  created_at: string;
}

interface FeaturedArtworksProps {
  onAddToCart: (id: number) => void;
}

export default function FeaturedArtworks({ onAddToCart }: FeaturedArtworksProps) {
  const [likedItems, setLikedItems] = useState<number[]>([]);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const [showArtworkModal, setShowArtworkModal] = useState(false);

  // Mock data as fallback
  const mockArtworks = [
    {
      id: 1,
      title: "Gündoğumu Yansımaları",
      artist: "Mehmet Aksoy",
      technique: "Yağlı Boya",
      category: "oil",
      size: "70x50 cm",
      price: "8500",
      year: "2023",
      materials: "Tuval üzerine yağlı boya",
      description: "Bu eser, doğanın en büyüleyici anlarından biri olan gündoğumunu yansıtır.",
      image_url: "https://public.readdy.ai/ai/img_res/749692079314eebeda52282f6a72e896.jpg",
      created_at: "2024-01-01"
    },
    {
      id: 2,
      title: "Sakin Orman",
      artist: "Ayşe Demir",
      technique: "Sulu Boya",
      category: "watercolor",
      size: "60x40 cm",
      price: "4200",
      year: "2024",
      materials: "Sulu boya kağıdı üzerine sulu boya",
      description: "Ormanın derinliklerinde yakalanan bu sessizlik, sulu boyanın şeffaflığı ile mükemmel bir uyum içinde sunulmuştur.",
      image_url: "https://readdy.ai/api/search-image?query=Peaceful%20forest%20landscape%20watercolor%20painting%2C%20transparent%20watercolor%20technique%20showing%20tall%20trees%20and%20dappled%20sunlight%2C%20soft%20green%20and%20blue%20watercolor%20washes%2C%20delicate%20watercolor%20paper%20texture%2C%20serene%20woodland%20scene%20with%20flowing%20watercolor%20effects&width=400&height=500&seq=art2&orientation=portrait",
      created_at: "2024-01-02"
    },
    {
      id: 3,
      title: "Şehir Işıkları",
      artist: "Can Yılmaz",
      technique: "Yağlı Boya",
      category: "oil",
      size: "80x60 cm",
      price: "12000",
      year: "2023",
      materials: "Tuval üzerine yağlı boya",
      description: "Modern şehir yaşamının dinamizmini yansıtan bu eser, gece ışıklarının büyüsünü tuval üzerine taşır.",
      image_url: "https://readdy.ai/api/search-image?query=Urban%20city%20lights%20oil%20painting%20at%20night%2C%20vibrant%20city%20illumination%20with%20colorful%20neon%20reflections%2C%20contemporary%20oil%20painting%20technique%20with%20bold%20brushstrokes%2C%20dynamic%20urban%20landscape%20with%20glowing%20street%20lights%20and%20buildings%2C%20modern%20cityscape%20artwork&width=400&height=500&seq=art3&orientation=portrait",
      created_at: "2024-01-03"
    },
    {
      id: 4,
      title: "Çiçekli Bahçe",
      artist: "Zehra Kaya",
      technique: "Sulu Boya",
      category: "watercolor",
      size: "50x35 cm",
      price: "3800",
      year: "2024",
      materials: "Sulu boya kağıdı üzerine sulu boya",
      description: "Çiçeklerin renk cümbüşü, sulu boyanın akışkanlığı ile hayat bulur.",
      image_url: "https://readdy.ai/api/search-image?query=Colorful%20flower%20garden%20watercolor%20painting%2C%20vibrant%20blooming%20flowers%20in%20watercolor%20technique%2C%20soft%20transparent%20watercolor%20layers%20showing%20garden%20blooms%2C%20delicate%20floral%20watercolor%20artwork%20with%20flowing%20colors%2C%20spring%20garden%20scene%20in%20watercolor%20medium&width=400&height=500&seq=art4&orientation=portrait",
      created_at: "2024-01-04"
    },
    {
      id: 5,
      title: "Dağ Manzarası",
      artist: "Okan Şahin",
      technique: "Yağlı Boya",
      category: "oil",
      size: "90x70 cm",
      price: "15500",
      year: "2023",
      materials: "Tuval üzerine yağlı boya",
      description: "Dağların heybeti ve doğanın gücü, cesur fırça darbeleriyle ifade edilir.",
      image_url: "https://readdy.ai/api/search-image?query=Majestic%20mountain%20landscape%20oil%20painting%2C%20dramatic%20mountain%20peaks%20with%20clouds%20and%20sky%2C%20realistic%20oil%20painting%20technique%20with%20detailed%20brushwork%2C%20panoramic%20mountain%20view%20with%20rich%20oil%20paint%20textures%2C%20professional%20landscape%20artwork%20with%20depth%20and%20atmosphere&width=400&height=500&seq=art5&orientation=portrait",
      created_at: "2024-01-05"
    },
    {
      id: 6,
      title: "Deniz Kıyısı",
      artist: "Elif Özkan",
      technique: "Sulu Boya",
      category: "watercolor",
      size: "55x40 cm",
      price: "4800",
      year: "2024",
      materials: "Sulu boya kağıdı üzerine sulu boya",
      description: "Denizin sakinliği ve dalgaların ritmi, sulu boyanın şeffaflığında yaşanır.",
      image_url: "https://readdy.ai/api/search-image?query=Serene%20seashore%20watercolor%20painting%2C%20gentle%20waves%20meeting%20sandy%20beach%20in%20watercolor%2C%20soft%20blue%20and%20beige%20watercolor%20washes%2C%20transparent%20watercolor%20technique%20capturing%20ocean%20atmosphere%2C%20peaceful%20coastal%20scene%20with%20flowing%20watercolor%20effects&width=400&height=500&seq=art6&orientation=portrait",
      created_at: "2024-01-06"
    }
  ];

  // Fiyat formatlama fonksiyonu
  const formatPrice = (price: string | number) => {
    // Eğer zaten formatlanmışsa olduğu gibi döndür
    if (typeof price === 'string' && price.includes('TL+KDV')) {
      return price;
    }
    
    // Sayısal değeri al
    const numericPrice = typeof price === 'string' ? parseFloat(price.replace(/[^\d]/g, '')) : price;
    
    // Formatla ve TL+KDV ekle
    return `${numericPrice.toLocaleString('tr-TR')} TL+KDV`;
  };

  useEffect(() => {
    fetchFeaturedArtworks();
    checkUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchUserFavorites();
    }
  }, [user]);

  const fetchFeaturedArtworks = async () => {
    try {
      const { data, error } = await supabase
        .from('artworks')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(6); // Sadece 6 eser göster

      if (error) throw error;
      
      // Eğer veritabanında eser yoksa mock data kullan
      setArtworks(data && data.length > 0 ? data : mockArtworks);
    } catch (error) {
      console.error('Error fetching featured artworks:', error);
      // Hata durumunda mock data kullan
      setArtworks(mockArtworks);
    } finally {
      setIsLoading(false);
    }
  };

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setUser(session?.user || null);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
      if (session?.user) {
        fetchUserFavorites();
      } else {
        setLikedItems([]);
      }
    });

    return () => subscription.unsubscribe();
  };

  const fetchUserFavorites = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('artwork_id')
        .eq('user_id', user.id);

      if (error) throw error;

      const favoriteIds = data?.map(item => item.artwork_id) || [];
      setLikedItems(favoriteIds);
    } catch (error) {
      console.error('Error fetching user favorites:', error);
    }
  };

  const toggleLike = async (id: number) => {
    if (!user) {
      // Kullanıcı giriş yapmamışsa ana sayfaya yönlendir
      return;
    }

    const isLiked = likedItems.includes(id);

    try {
      if (isLiked) {
        // Favorilerden kaldır
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('artwork_id', id);

        if (error) throw error;

        setLikedItems(prev => prev.filter(item => item !== id));
      } else {
        // Favorilere ekle
        const { error } = await supabase
          .from('favorites')
          .insert([{ user_id: user.id, artwork_id: id }]);

        if (error) throw error;

        setLikedItems(prev => [...prev, id]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const addToCart = (id: number) => {
    // Ana bileşene sepete ekleme işlemini bildir
    onAddToCart(id);
    
    // Görsel geri bildirim
    const button = document.querySelector(`[data-cart-btn="${id}"]`);
    if (button) {
      const originalText = button.textContent;
      const originalClasses = button.className;
      
      // Change to green and show success message
      button.textContent = 'Eklendi!';
      button.className = button.className.replace('bg-white text-black', 'bg-green-500 text-white');
      
      setTimeout(() => {
        button.textContent = originalText;
        button.className = originalClasses;
      }, 2000);
    }
  };

  const openArtworkModal = (artwork: Artwork) => {
    setSelectedArtwork(artwork);
    setShowArtworkModal(true);
  };

  if (isLoading) {
    return (
      <section id="featured-artworks-section" className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-gray-300">Öne çıkan eserler yükleniyor...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="featured-artworks-section" className="py-20 bg-black" data-product-shop>
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Öne Çıkan Eserler</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Sanatçılarımızın en sevilen ve beğenilen eserlerinden özenle seçilmiş koleksiyon. 
              Her eser özgün sertifikası ile birlikte teslim edilir.
            </p>
          </div>

          {artworks.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-image-line text-6xl text-gray-600 mb-4"></i>
              <h3 className="text-2xl font-bold mb-2">Henüz eser eklenmemiş</h3>
              <p className="text-gray-400">Admin panelinden eserlerinizi ekleyerek başlayın</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {artworks.map((artwork) => (
                <div 
                  key={artwork.id} 
                  className="group bg-gray-900 rounded-lg overflow-hidden hover:bg-gray-800 transition-colors cursor-pointer"
                  onClick={() => openArtworkModal(artwork)}
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img 
                      src={artwork.image_url || "https://readdy.ai/api/search-image?query=Abstract%20art%20painting%20placeholder%2C%20artistic%20background%20with%20paint%20textures%2C%20creative%20artwork%20display%2C%20gallery%20quality%20art%20piece&width=400&height=500&seq=placeholder&orientation=portrait"}
                      alt={artwork.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 flex gap-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleLike(artwork.id);
                        }}
                        className={`w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-sm transition-colors cursor-pointer ${
                          likedItems.includes(artwork.id) 
                            ? 'bg-red-500/80 text-white' 
                            : 'bg-black/50 text-gray-300 hover:text-red-400'
                        }`}
                      >
                        <i className={`ri-heart-${likedItems.includes(artwork.id) ? 'fill' : 'line'} text-lg`}></i>
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          openArtworkModal(artwork);
                        }}
                        className="w-10 h-10 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-full text-gray-300 hover:text-white transition-colors cursor-pointer"
                      >
                        <i className="ri-eye-line text-lg"></i>
                      </button>
                    </div>
                    <div className="absolute bottom-4 left-4">
                      <span className="bg-white/90 text-black text-xs px-3 py-1 rounded-full font-medium">
                        {artwork.technique}
                      </span>
                    </div>
                    {/* Multiple Images Indicator */}
                    {artwork.images && artwork.images.length > 1 && (
                      <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <i className="ri-image-line"></i>
                        {artwork.images.length}
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-gray-300 transition-colors">
                      {artwork.title}
                    </h3>
                    <p className="text-gray-400 mb-1">Sanatçı: {artwork.artist}</p>
                    <p className="text-gray-500 text-sm mb-4">Boyut: {artwork.size}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-white">{formatPrice(artwork.price)}</span>
                      <button 
                        data-cart-btn={artwork.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(artwork.id);
                        }}
                        className="bg-white text-black px-6 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Sepete Ekle
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Artwork Detail Modal */}
      <ArtworkModal
        artwork={selectedArtwork}
        isOpen={showArtworkModal}
        onClose={() => setShowArtworkModal(false)}
        onAddToCart={addToCart}
        onToggleLike={toggleLike}
        isLiked={selectedArtwork ? likedItems.includes(selectedArtwork.id) : false}
      />
    </>
  );
}
